import { useState, useRef, useEffect } from "react";

import { useSelector } from "react-redux";

import { getMessagePreviewById } from "utils/message";
import { handleScrollIntoView } from "utils/view";
import { useOutsideClick } from "hooks/useOutsideClick";
import styles from "./styles.module.scss";
import ProgressBar from "components/ProgressBar";

const getLocale = ({ settingsData }) => settingsData.locale;
const getSelectedConversation = ({ chatsData }) =>
  chatsData.selectedConversation;

const SPEED_OPTIONS = [
  {
    label: "0.5x",
    value: 0.5,
  },
  {
    label: "1x",
    value: 1,
  },
  {
    label: "1.5x",
    value: 1.5,
  },
  {
    label: "2x",
    value: 2,
  },
];

const INITIAL_CUSTOM_SPEED = SPEED_OPTIONS[1];

const AudioMessage = ({
  id,
  url,
  replyId,
  // status,
  // time,
  progress,
  delivered,
}) => {
  const [playbackSpeed, setPlaybackSpeed] = useState(INITIAL_CUSTOM_SPEED);
  const [showPlaybackMenu, setShowPlaybackMenu] = useState(false);
  const audioElementRef = useRef();
  const playbackMenuRef = useRef();
  const [isAppleDevice, setIsAppleDevice] = useState(false);
  const [mp3Url, setMp3Url] = useState(null);
  useOutsideClick(playbackMenuRef, () => setShowPlaybackMenu(false));

  const locale = useSelector(getLocale);
  const selectedConversation = useSelector(getSelectedConversation);

  const handleShowRepliedMessage = () => {
    handleScrollIntoView(replyId);
  };

  const handleClickCustomSpeedButton = (item) => {
    audioElementRef.current.playbackRate = item.value;

    setPlaybackSpeed(item);
    setShowPlaybackMenu(false);
  };

  const handleClickPlaybackTriggerBtn = (e) => {
    e.stopPropagation();
    setShowPlaybackMenu((prev) => !prev);
  };

  const audioBufferToWav = (buffer) => {
    const numberOfChannels = buffer.numberOfChannels;
    const length = buffer.length * numberOfChannels * 2;
    const arrayBuffer = new ArrayBuffer(44 + length);
    const view = new DataView(arrayBuffer);

    const writeString = (view, offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    // Write WAV header
    writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + length, true);
    writeString(view, 8, "WAVE");
    writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, buffer.sampleRate, true);
    view.setUint32(28, buffer.sampleRate * 2 * numberOfChannels, true);
    view.setUint16(32, numberOfChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, "data");
    view.setUint32(40, length, true);

    // Write audio data
    const channelData = [];
    for (let channel = 0; channel < numberOfChannels; channel++) {
      channelData.push(buffer.getChannelData(channel));
    }

    let offset = 44;
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channelData[channel][i]));
        view.setInt16(
          offset,
          sample < 0 ? sample * 0x8000 : sample * 0x7fff,
          true
        );
        offset += 2;
      }
    }

    return new Blob([view], { type: "audio/wav" });
  };

  const convertUrlToMp3 = async (url) => {
    try {
      // Fetch the audio file
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch audio file");

      const arrayBuffer = await response.arrayBuffer();

      // Create AudioContext
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();

      // Decode the audio
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // Create offline context
      const offlineContext = new OfflineAudioContext(
        audioBuffer.numberOfChannels,
        audioBuffer.length,
        audioBuffer.sampleRate
      );

      // Create buffer source
      const source = offlineContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(offlineContext.destination);

      // Start and render
      source.start(0);
      const renderedBuffer = await offlineContext.startRendering();

      // Convert to WAV first (intermediate step)
      const wavBlob = audioBufferToWav(renderedBuffer);

      // Convert to MP3
      const mp3Blob = new Blob([wavBlob], { type: "audio/mp3" });

      // Create URL for the MP3
      const mp3Url = URL.createObjectURL(mp3Blob);
      setMp3Url(mp3Url);
    } catch (err) {}
  };

  const renderPlaybackSpeedMenuItems = SPEED_OPTIONS.map((item) => (
    <span
      data-selected={item.value === playbackSpeed.value}
      className={styles.speed_control__option}
      key={item.label}
      onClick={() => handleClickCustomSpeedButton(item)}
    >
      {item.label}
    </span>
  ));

  const renderProgressIndicator = !delivered &&
    ![null, undefined].includes(progress) && (
      <div className={styles.progress_bar_wrapper}>
        <ProgressBar
          progress={progress}
          trackColor="#FFF"
          trackWidth={4}
          size={30}
          indicatorWidth={4}
        />
      </div>
    );

  // Check if device is iOS
  useEffect(() => {
    const checkDevice = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isAppleDevice = /iphone|ipad|ipod|macintosh|mac os x/i.test(
        userAgent
      );
      setIsAppleDevice(isAppleDevice);
    };

    checkDevice();
  }, []);

  // Convert audio into mp3 on ios
  useEffect(() => {
    if (isAppleDevice) convertUrlToMp3(url);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppleDevice]);

  return (
    <div
      data-id={id}
      className="conv__body__body__inner__msgright__inner__bubble__voice flex flex-column"
    >
      {replyId && (
        <div
          onClick={handleShowRepliedMessage}
          dir="auto"
          className="conv__body__body__inner__msgright__inner__bubble__replied_msg"
        >
          <span>
            {getMessagePreviewById(replyId, selectedConversation, locale)}
          </span>
        </div>
      )}
      <audio
        ref={audioElementRef}
        controls
        src={isAppleDevice && mp3Url ? mp3Url : undefined}
      >
        {!isAppleDevice && (
          <>
            <source src={url} type="audio/ogg" />
            <source src={url} type="audio/mpeg" />
            Your browser does not support the audio tag.
          </>
        )}
      </audio>

      <div
        ref={playbackMenuRef}
        data-show={showPlaybackMenu}
        className={styles.speed_control}
      >
        <div className={styles.speed_control__menu}>
          <div className={styles.speed_control__menu_inner}>
            {renderPlaybackSpeedMenuItems}
          </div>
        </div>
        <div
          className={styles.speed_control__trigger}
          onClick={handleClickPlaybackTriggerBtn}
        >
          {playbackSpeed.label}
        </div>
      </div>
      {renderProgressIndicator}
    </div>
  );
};

export default AudioMessage;
